import { useParams, Link } from 'react-router-dom'
import { useEffect } from 'react'
import { FileText, Image, QrCode, Type, Palette, Clock, Ruler } from 'lucide-react'
import Card from '../components/Card'

// Import tool components
import ImageConverter from '../tools/ImageConverter'
import ImageResizer from '../tools/ImageResizer'
import ImageCompressor from '../tools/ImageCompressor'
import ImageCropper from '../tools/ImageCropper'
import BackgroundRemover from '../tools/BackgroundRemover'
import ImageEditor from '../tools/ImageEditor'
import QRGenerator from '../tools/QRGenerator'
import BarcodeGenerator from '../tools/BarcodeGenerator'
import QRScanner from '../tools/QRScanner'
import WiFiQRGenerator from '../tools/WiFiQRGenerator'
import ContactQRGenerator from '../tools/ContactQRGenerator'
import CaseConverter from '../tools/CaseConverter'
import WordCounter from '../tools/WordCounter'
import TextFormatter from '../tools/TextFormatter'
import TextReverser from '../tools/TextReverser'
import TextAnalyzer from '../tools/TextAnalyzer'
import TextGenerator from '../tools/TextGenerator'
import TextEncoder from '../tools/TextEncoder'
import TextDiff from '../tools/TextDiff'
import ColorPicker from '../tools/ColorPicker'
import ColorConverter from '../tools/ColorConverter'
import PaletteGenerator from '../tools/PaletteGenerator'
import ColorSchemes from '../tools/ColorSchemes'
import ColorBlindness from '../tools/ColorBlindness'
import ColorContrast from '../tools/ColorContrast'
import TimezoneConverter from '../tools/TimezoneConverter'
import DateCalculator from '../tools/DateCalculator'
import CountdownTimer from '../tools/CountdownTimer'
import Stopwatch from '../tools/Stopwatch'
import AgeCalculator from '../tools/AgeCalculator'
import WorkingDays from '../tools/WorkingDays'
import WorldClock from '../tools/WorldClock'
import TimeConverter from '../tools/TimeConverter'
import LengthConverter from '../tools/LengthConverter'
import WeightConverter from '../tools/WeightConverter'
import TemperatureConverter from '../tools/TemperatureConverter'
import VolumeConverter from '../tools/VolumeConverter'
import AreaConverter from '../tools/AreaConverter'
import SpeedConverter from '../tools/SpeedConverter'
import EnergyConverter from '../tools/EnergyConverter'
import PressureConverter from '../tools/PressureConverter'
import PDFMerger from '../tools/PDFMerger'
import PDFSplitter from '../tools/PDFSplitter'
import PDFCompressor from '../tools/PDFCompressor'
import PDFToImage from '../tools/PDFToImage'
import PDFRotator from '../tools/PDFRotator'
import PDFWatermark from '../tools/PDFWatermark'

const toolData = {
  pdf: {
    name: 'PDF Tools',
    icon: FileText,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    tools: [
      { name: 'PDF Merger', description: 'Combine multiple PDF files into one document', id: 'pdf-merger' },
      { name: 'PDF Splitter', description: 'Split PDF into separate files by pages', id: 'pdf-splitter' },
      { name: 'PDF Compressor', description: 'Reduce PDF file size while maintaining quality', id: 'pdf-compressor' },
      { name: 'PDF to Image', description: 'Convert PDF pages to image files (JPG, PNG)', id: 'pdf-to-image' },
      { name: 'PDF Rotator', description: 'Rotate PDF pages to correct orientation', id: 'pdf-rotator' },
      { name: 'PDF Watermark', description: 'Add watermarks to PDF documents', id: 'pdf-watermark' },
    ],
  },
  image: {
    name: 'Image Tools',
    icon: Image,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    tools: [
      { name: 'Image Converter', description: 'Convert between image formats (JPG, PNG, WebP, GIF, BMP)', id: 'image-converter' },
      { name: 'Image Compressor', description: 'Reduce image file size without losing quality', id: 'image-compressor' },
      { name: 'Image Resizer', description: 'Resize images to specific dimensions or percentages', id: 'image-resizer' },
      { name: 'Image Cropper', description: 'Crop images to desired size and aspect ratio', id: 'image-cropper' },
      { name: 'Background Remover', description: 'Remove background from images automatically', id: 'image-background-remover' },
      { name: 'Image Editor', description: 'Edit images with filters, brightness, contrast, and more', id: 'image-editor' },
    ],
  },
  qr: {
    name: 'QR & Barcode Tools',
    icon: QrCode,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    tools: [
      { name: 'QR Generator', description: 'Generate QR codes for text, URLs, and more', id: 'qr-generator' },
      { name: 'Barcode Generator', description: 'Create barcodes in various formats', id: 'barcode-generator' },
      { name: 'QR Scanner', description: 'Scan and decode QR codes from images', id: 'qr-scanner' },
      { name: 'WiFi QR Generator', description: 'Generate QR codes for WiFi network credentials', id: 'wifi-qr' },
      { name: 'Contact QR Generator', description: 'Create QR codes for contact information (vCard)', id: 'contact-qr' },
      { name: 'QR Customizer', description: 'Customize QR code colors, patterns, and logos', id: 'qr-customizer' },
    ],
  },
  text: {
    name: 'Text Tools',
    icon: Type,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    tools: [
      { name: 'Case Converter', description: 'Convert text between uppercase, lowercase, and title case', id: 'case-converter' },
      { name: 'Word Counter', description: 'Count words, characters, sentences, and paragraphs', id: 'word-counter' },
      { name: 'Text Formatter', description: 'Format and clean up text with various options', id: 'text-formatter' },
      { name: 'Text Reverser', description: 'Reverse text characters or words', id: 'text-reverser' },
      { name: 'Text Analyzer', description: 'Analyze text statistics and readability metrics', id: 'text-analyzer' },
      { name: 'Text Generator', description: 'Generate Lorem Ipsum and placeholder text', id: 'text-generator' },
      { name: 'Text Encoder/Decoder', description: 'Encode and decode text in various formats', id: 'text-encoder' },
      { name: 'Text Diff', description: 'Compare and find differences between two texts', id: 'text-diff' },
    ],
  },
  color: {
    name: 'Color Tools',
    icon: Palette,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    tools: [
      { name: 'Color Picker', description: 'Pick colors from images or use color picker tool', id: 'color-picker' },
      { name: 'Color Converter', description: 'Convert between HEX, RGB, HSL, and other color formats', id: 'color-converter' },
      { name: 'Palette Generator', description: 'Generate color palettes from images or manually', id: 'palette-generator' },
      { name: 'Color Schemes', description: 'Generate harmonious color schemes and combinations', id: 'color-schemes' },
      { name: 'Color Blindness Simulator', description: 'Simulate how colors appear to colorblind users', id: 'color-blindness' },
      { name: 'Color Contrast Checker', description: 'Check color contrast ratios for accessibility', id: 'color-contrast' },
    ],
  },
  time: {
    name: 'Time Tools',
    icon: Clock,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    tools: [
      { name: 'Timezone Converter', description: 'Convert time between different timezones', id: 'timezone-converter' },
      { name: 'Date Calculator', description: 'Calculate date differences and add/subtract days', id: 'date-calculator' },
      { name: 'Countdown Timer', description: 'Create countdown timers for events', id: 'countdown-timer' },
      { name: 'Stopwatch', description: 'Precise stopwatch with lap timing', id: 'stopwatch' },
      { name: 'Age Calculator', description: 'Calculate age from birthdate', id: 'age-calculator' },
      { name: 'Working Days Calculator', description: 'Calculate working days between dates', id: 'working-days' },
      { name: 'World Clock', description: 'View current time in multiple timezones', id: 'world-clock' },
      { name: 'Time Converter', description: 'Convert between different time units', id: 'time-converter' },
    ],
  },
  unit: {
    name: 'Unit Converter',
    icon: Ruler,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    tools: [
      { name: 'Length Converter', description: 'Convert between meters, feet, inches, and more', id: 'length-converter' },
      { name: 'Weight Converter', description: 'Convert between kilograms, pounds, ounces, and more', id: 'weight-converter' },
      { name: 'Temperature Converter', description: 'Convert between Celsius, Fahrenheit, and Kelvin', id: 'temperature-converter' },
      { name: 'Volume Converter', description: 'Convert between liters, gallons, cups, and more', id: 'volume-converter' },
      { name: 'Area Converter', description: 'Convert between square meters, square feet, acres, and more', id: 'area-converter' },
      { name: 'Speed Converter', description: 'Convert between km/h, mph, m/s, and more', id: 'speed-converter' },
      { name: 'Energy Converter', description: 'Convert between joules, calories, kWh, and more', id: 'energy-converter' },
      { name: 'Pressure Converter', description: 'Convert between Pascal, PSI, bar, and more', id: 'pressure-converter' },
    ],
  },
}

// Tool component mapping - keys must match: category-toolName format
const toolComponents = {
  'image-converter': ImageConverter,
  'image-resizer': ImageResizer,
  'image-compressor': ImageCompressor,
  'image-cropper': ImageCropper,
  'image-background-remover': BackgroundRemover,
  'image-editor': ImageEditor,
  'qr-qr-generator': QRGenerator,
  'qr-generator': QRGenerator, // Alternative mapping
  'qr-barcode-generator': BarcodeGenerator,
  'qr-qr-scanner': QRScanner,
  'qr-scanner': QRScanner, // Alternative mapping
  'qr-wifi-qr': WiFiQRGenerator,
  'qr-contact-qr': ContactQRGenerator,
  'qr-qr-customizer': QRGenerator, // QR Customizer uses the same component with customization features
  'qr-customizer': QRGenerator, // Alternative mapping
  'text-case-converter': CaseConverter,
  'text-word-counter': WordCounter,
  'text-text-formatter': TextFormatter,
  'text-formatter': TextFormatter, // Alternative mapping
  'text-text-reverser': TextReverser,
  'text-reverser': TextReverser, // Alternative mapping
  'text-text-analyzer': TextAnalyzer,
  'text-analyzer': TextAnalyzer, // Alternative mapping
  'text-text-generator': TextGenerator,
  'text-generator': TextGenerator, // Alternative mapping
  'text-text-encoder': TextEncoder,
  'text-encoder': TextEncoder, // Alternative mapping
  'text-text-diff': TextDiff,
  'text-diff': TextDiff, // Alternative mapping
  'color-color-picker': ColorPicker,
  'color-picker': ColorPicker, // Alternative mapping
  'color-color-converter': ColorConverter,
  'color-converter': ColorConverter, // Alternative mapping
  'color-palette-generator': PaletteGenerator,
  'color-color-schemes': ColorSchemes,
  'color-schemes': ColorSchemes, // Alternative mapping
  'color-color-blindness': ColorBlindness,
  'color-blindness': ColorBlindness, // Alternative mapping
  'color-color-contrast': ColorContrast,
  'color-contrast': ColorContrast, // Alternative mapping
  'time-timezone-converter': TimezoneConverter,
  'timezone-converter': TimezoneConverter, // Alternative mapping
  'time-date-calculator': DateCalculator,
  'date-calculator': DateCalculator, // Alternative mapping
  'time-countdown-timer': CountdownTimer,
  'countdown-timer': CountdownTimer, // Alternative mapping
  'time-stopwatch': Stopwatch,
  'stopwatch': Stopwatch, // Alternative mapping
  'time-age-calculator': AgeCalculator,
  'age-calculator': AgeCalculator, // Alternative mapping
  'time-working-days': WorkingDays,
  'working-days': WorkingDays, // Alternative mapping
  'time-world-clock': WorldClock,
  'world-clock': WorldClock, // Alternative mapping
  'time-time-converter': TimeConverter,
  'time-converter': TimeConverter, // Alternative mapping
  'unit-length-converter': LengthConverter,
  'length-converter': LengthConverter, // Alternative mapping
  'unit-weight-converter': WeightConverter,
  'weight-converter': WeightConverter, // Alternative mapping
  'unit-temperature-converter': TemperatureConverter,
  'temperature-converter': TemperatureConverter, // Alternative mapping
  'unit-volume-converter': VolumeConverter,
  'volume-converter': VolumeConverter, // Alternative mapping
  'unit-area-converter': AreaConverter,
  'area-converter': AreaConverter, // Alternative mapping
  'unit-speed-converter': SpeedConverter,
  'speed-converter': SpeedConverter, // Alternative mapping
  'unit-energy-converter': EnergyConverter,
  'energy-converter': EnergyConverter, // Alternative mapping
  'unit-pressure-converter': PressureConverter,
  'pressure-converter': PressureConverter, // Alternative mapping
  'pdf-pdf-merger': PDFMerger,
  'pdf-merger': PDFMerger, // Alternative mapping
  'pdf-pdf-splitter': PDFSplitter,
  'pdf-splitter': PDFSplitter, // Alternative mapping
  'pdf-pdf-compressor': PDFCompressor,
  'pdf-compressor': PDFCompressor, // Alternative mapping
  'pdf-pdf-to-image': PDFToImage,
  'pdf-to-image': PDFToImage, // Alternative mapping
  'pdf-pdf-rotator': PDFRotator,
  'pdf-rotator': PDFRotator, // Alternative mapping
  'pdf-pdf-watermark': PDFWatermark,
  'pdf-watermark': PDFWatermark, // Alternative mapping
}

export default function ToolDetail() {
  const { category, toolName } = useParams()
  const categoryData = toolData[category] || { 
    name: 'Tools', 
    tools: [], 
    icon: FileText, 
    color: 'text-gray-600', 
    bgColor: 'bg-gray-50' 
  }

  const Icon = categoryData.icon

  // Update SEO meta tags dynamically
  useEffect(() => {
    if (toolName) {
      const fullToolId = `${category}-${toolName}`
      const tool = categoryData.tools.find(t => t.id === fullToolId || t.id === toolName || t.id === `${category}-${toolName}`)
      const toolDisplayName = tool?.name || toolName
      const toolDescription = tool?.description || `Free online ${toolDisplayName} tool`
      
      // Update document title
      document.title = `${toolDisplayName} - Free Online ${categoryData.name} | ToolsBucket`
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]')
      if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.setAttribute('name', 'description')
        document.head.appendChild(metaDescription)
      }
      metaDescription.setAttribute('content', `${toolDescription}. Free online tool, no registration required. Fast, secure, and completely free.`)
      
      // Update or create Open Graph tags
      const updateOGTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`)
        if (!tag) {
          tag = document.createElement('meta')
          tag.setAttribute('property', property)
          document.head.appendChild(tag)
        }
        tag.setAttribute('content', content)
      }
      
      updateOGTag('og:title', `${toolDisplayName} - Free Online Tool`)
      updateOGTag('og:description', toolDescription)
      updateOGTag('og:url', `https://toolsbucket.net/tools/${category}/${toolName}`)
      
      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', `https://toolsbucket.net/tools/${category}/${toolName}`)
    } else if (category) {
      // Category page
      document.title = `${categoryData.name} - Free Online Tools | ToolsBucket`
      let metaDescription = document.querySelector('meta[name="description"]')
      if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.setAttribute('name', 'description')
        document.head.appendChild(metaDescription)
      }
      metaDescription.setAttribute('content', `Free online ${categoryData.name.toLowerCase()}. Access all ${categoryData.tools.length} tools for free. No registration required.`)
    }
    
    // Cleanup function to reset title on unmount
    return () => {
      document.title = 'Free Online Tools - PDF, Image, QR Code, Text & Unit Converter Tools | ToolsBucket'
    }
  }, [category, toolName, categoryData])

  // If toolName is provided, render the specific tool
  if (toolName) {
    // Construct the full tool ID (category-toolName)
    const fullToolId = `${category}-${toolName}`
    // Also try with the toolName as-is (in case it already includes category)
    const alternativeToolId = toolName.startsWith(`${category}-`) ? toolName : fullToolId
    const ToolComponent = toolComponents[fullToolId] || toolComponents[alternativeToolId] || toolComponents[toolName]
    
    if (ToolComponent) {
      // Find tool by matching either fullToolId or just toolName (for backward compatibility)
      const tool = categoryData.tools.find(t => t.id === fullToolId || t.id === toolName || t.id === `${category}-${toolName}` || t.id === alternativeToolId)
      return (
        <div className="space-y-6">
          <div className="flex items-center space-x-4 mb-2">
            <div className={`w-12 h-12 ${categoryData.bgColor} rounded-xl flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${categoryData.color}`} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {tool?.name || toolName}
              </h1>
              {tool?.description && (
                <p className="text-gray-600 mt-1">{tool.description}</p>
              )}
            </div>
          </div>
          <ToolComponent />
        </div>
      )
    } else {
      // Tool component not found, show placeholder
      return (
        <div className="space-y-6">
          <Card>
            <Card.Body className="text-center py-12">
              <p className="text-gray-600 mb-4">Tool "{toolName}" is being implemented.</p>
              <p className="text-sm text-gray-500">Please check back soon!</p>
            </Card.Body>
          </Card>
        </div>
      )
    }
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 mb-2">
        <div className={`w-12 h-12 ${categoryData.bgColor} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${categoryData.color}`} />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{categoryData.name}</h1>
          <p className="text-gray-600">Select a tool to get started</p>
        </div>
      </div>

      {categoryData.tools.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-12">
            <p className="text-gray-600 mb-4">No tools available for this category yet.</p>
            <p className="text-sm text-gray-500">Check back soon for new tools!</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categoryData.tools.map((tool, index) => (
            <Link
              key={index}
              to={`/tools/${category}/${tool.id.replace(`${category}-`, '')}`}
              className="block"
            >
              <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer group h-full">
                <Card.Body>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 ${categoryData.bgColor} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-5 h-5 ${categoryData.color}`} />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{tool.description}</p>
                </Card.Body>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

